import React, { useEffect, useRef, useState } from 'react';
import {
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  Info,
} from 'lucide-react';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import {
  describeFlags,
  decisionMethodSentence,
  escalationNote,
  describeAdvisor,
} from './constants';
import MatchIcon from './MatchIcon';
import LabelPanel from './LabelPanel';

// Decision timestamps come back from Mongo/FastAPI as naive UTC (no 'Z' or
// offset suffix) even though they're written as tz-aware UTC — Mongo strips
// tzinfo on write. date-fns' parseISO treats an offset-less string as LOCAL
// time, so without this every timestamp in the drawer drifts by the
// browser's UTC offset. Treat any string missing a timezone designator as
// UTC before parsing. Mirrors the same helper in DecisionsTab.js.
function parseUtc(iso) {
  if (!iso) return null;
  const hasOffset = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso);
  return parseISO(hasOffset ? iso : `${iso}Z`);
}

function formatReceived(iso) {
  if (!iso) return '—';
  try {
    const date = parseUtc(iso);
    return `${format(date, 'MMM d, yyyy h:mm a')} (${formatDistanceToNow(date, { addSuffix: true })})`;
  } catch {
    return iso;
  }
}

const STATUS_META = {
  auto_routed: { label: 'Auto-routed', cls: 'boss-chip-success' },
  manual_review: { label: 'Manual review', cls: 'boss-chip-warning' },
  pending: { label: 'Pending', cls: 'boss-chip-neutral' },
  failed: { label: 'Failed', cls: 'boss-chip-danger' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return <span className="text-boss-text-muted">—</span>;
  return <span className={`boss-chip ${meta.cls}`}>{meta.label}</span>;
}

function ConfidenceBadge({ val }) {
  if (val == null) return <span className="text-boss-text-muted">—</span>;
  const pct = Math.round(val * 100);
  let cls = 'boss-chip-success';
  if (val < 0.7) cls = 'boss-chip-danger';
  else if (val < 0.85) cls = 'boss-chip-warning';
  return <span className={`boss-chip ${cls}`}>{pct}%</span>;
}

function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-boss-border rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-boss-elevated hover:bg-boss-surface transition-colors text-sm font-medium text-boss-text"
      >
        <span>{title}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && <div className="px-4 py-3 border-t border-boss-border">{children}</div>}
    </div>
  );
}

const OFF_ENUM_FIELD_LABELS = {
  human_department: 'Department',
  human_ticket_type: 'Ticket type',
};

// `human_off_enum_fields` carries column names; a customer reading this screen
// should not have to.
function offEnumFieldLabels(fields) {
  const names = (Array.isArray(fields) ? fields : [])
    .map((f) => OFF_ENUM_FIELD_LABELS[f])
    .filter(Boolean);
  if (names.length === 0) return 'This label is';
  if (names.length === 1) return `${names[0]} is`;
  return `${names.join(' and ')} are`;
}

function Kbd({ children }) {
  return (
    <kbd className="px-1.5 py-0.5 rounded border border-boss-border bg-boss-surface text-[10px] font-medium text-boss-text">
      {children}
    </kbd>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-3 py-1.5 border-b border-boss-border last:border-0">
      <span className="text-xs text-boss-text-secondary shrink-0 w-28">{label}</span>
      {/* min-w-0 + break-words: a flex child won't shrink below its longest
          unbreakable token by default, so a value like
          `haiku_fallback_after_sonnet_fail:SONNET_LLM_PARSE_FAIL` would push
          the row wider than the drawer and force a horizontal scrollbar. */}
      <span className="text-xs text-boss-text flex-1 min-w-0 break-words">{children}</span>
    </div>
  );
}

// Plain-letter shortcuts must not fire while a text box or a native select
// has focus — otherwise typing an owner's name pages through the queue.
function isTypingTarget(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

export default function DecisionDrawer({
  decision: d,
  onClose,
  canLabel = false,
  onLabeled,
  onNext,
  onPrev,
  position,
}) {
  const splits = Array.isArray(d.splits) ? d.splits : [];
  const flags = Array.isArray(d.flags) ? d.flags : [];
  const llmCalls = Array.isArray(d.llm_calls) ? d.llm_calls : [];

  // Flags, translated and ranked by what the reviewer has to act on. Cheap
  // enough not to memoise — a decision carries a handful of flags, not a feed.
  const notes = describeFlags(flags);
  const actionNotes = notes.filter((n) => n.severity === 'action');
  const contextNotes = notes.filter((n) => n.severity !== 'action');
  const multiRequestFlagged = notes.some((n) => n.name === 'MULTI_REQUEST_NEEDS_SPLIT');

  const methodSentence = decisionMethodSentence(d);
  // Null whenever `escalation_reason` is just the flag list again, which is
  // how the workflow builds it for every flagged decision.
  const escalation = escalationNote(d.escalation_reason, flags);

  const panelRef = useRef(null);

  // Whether saving jumps to the next decision. Lives here rather than in
  // LabelPanel so it survives the remount that navigating between decisions
  // triggers — a labeller sets it once for the whole session, not per row.
  const [advanceAfterSave, setAdvanceAfterSave] = useState(true);

  // Keyboard: Escape closes, J/K (or the arrow keys) walk the queue. A and S
  // belong to the label panel, which owns those actions and listens itself.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      const key = e.key.toLowerCase();
      if (key === 'j' || key === 'n' || e.key === 'ArrowDown') {
        if (onNext) { e.preventDefault(); onNext(); }
      } else if (key === 'k' || key === 'p' || e.key === 'ArrowUp') {
        if (onPrev) { e.preventDefault(); onPrev(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onNext, onPrev]);

  // Move focus into the drawer on open so the shortcuts work without a click
  // first, and hand it back to whatever opened it on close.
  useEffect(() => {
    const opener = document.activeElement;
    return () => {
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus();
      }
    };
  }, []);

  // Re-focus the panel whenever a different decision is shown. Keyed on the
  // id, not the object, so splicing a freshly-labelled row back into state
  // does not yank focus out of the owner box mid-word.
  useEffect(() => {
    panelRef.current?.focus();
  }, [d.dispatch_item_id]);

  // Auto-advance remounts LabelPanel, taking its "Saved" confirmation with it.
  // Hold the confirmation here, where it outlives the remount — otherwise a
  // successful save is indistinguishable from a row that simply jumped.
  const [saveNote, setSaveNote] = useState(null);

  const handleLabeled = (updated) => {
    onLabeled?.(updated);
    // Carries a timestamp so two identical confirmations in a row are still
    // distinct objects and each one restarts the dismiss timer.
    setSaveNote({
      at: Date.now(),
      text:
        advanceAfterSave && onNext
          ? 'Label saved — moved to the next decision.'
          : 'Label saved.',
    });
  };

  useEffect(() => {
    if (!saveNote) return undefined;
    const t = setTimeout(() => setSaveNote(null), 3000);
    return () => clearTimeout(t);
  }, [saveNote]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-boss-surface border-l border-boss-border shadow-2xl overflow-y-auto outline-none"
        role="dialog"
        aria-modal="true"
        aria-label="Decision details"
      >
        {/* Header */}
        <div className="sticky top-0 bg-boss-surface border-b border-boss-border px-5 py-4 flex items-center justify-between gap-3 z-10">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-boss-text">
              Decision Details
              {position && (
                <span className="ml-2 font-normal text-boss-text-muted">
                  {position.index + 1} of {position.total}
                </span>
              )}
            </h2>
            <p className="text-xs text-boss-text-secondary mt-0.5">{formatReceived(d.received_at)}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onPrev}
              disabled={!onPrev}
              className="p-2 rounded-lg hover:bg-boss-elevated transition-colors text-boss-text-muted hover:text-boss-text disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Previous decision"
              title="Previous decision (K)"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={onNext}
              disabled={!onNext}
              className="p-2 rounded-lg hover:bg-boss-elevated transition-colors text-boss-text-muted hover:text-boss-text disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Next decision"
              title="Next decision (J)"
            >
              <ChevronRight size={18} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-boss-elevated transition-colors text-boss-text-muted hover:text-boss-text"
              aria-label="Close"
              title="Close (Esc)"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Shortcut legend. Whoever is labelling on a call has not seen this
            screen before — the shortcuts are worthless if they are not on
            screen. */}
        {canLabel && (
          <div className="px-5 py-2 border-b border-boss-border bg-boss-elevated flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-boss-text-secondary">
            <span><Kbd>A</Kbd> AI was right</span>
            <span><Kbd>S</Kbd> save answers</span>
            <span><Kbd>J</Kbd>/<Kbd>K</Kbd> next / previous</span>
            <span><Kbd>Esc</Kbd> close</span>
          </div>
        )}

        {saveNote && (
          <div
            className="px-5 py-2 border-b border-boss-border boss-alert-warning text-xs"
            role="status"
            aria-live="polite"
          >
            {saveNote.text}
          </div>
        )}

        {/* flex + gap, not space-y: the dev server's visual-edits plugin wraps
            every `{cond && <section/>}` in a `display: contents` span, which
            has no box — space-y's margin lands on that span and is dropped,
            collapsing the gap. A contents span's child is itself the flex
            item, so `gap` spaces it the same in dev and production. */}
        <div className="px-5 py-5 flex flex-col gap-5">
          {/* AI decision. First thing in the drawer — the AI's own answer is
              what a reviewer is here to judge, so it leads rather than the
              raw email. */}
          <section>
            <h3 className="text-xs font-semibold text-boss-text-secondary uppercase tracking-wider mb-2">
              AI Decision
            </h3>
            <div className="glass-card p-3 space-y-0">
              <Row label="Status"><StatusBadge status={d.status} /></Row>
              <Row label="Department">{d.ai_department || '—'}</Row>
              <Row label="Ticket type">{d.ai_ticket_type || '—'}</Row>
              <Row label="Owner">{d.ai_owner || '—'}</Row>
              <Row label="Confidence"><ConfidenceBadge val={d.ai_confidence} /></Row>
              {/* `is_multi_request` and the MULTI_REQUEST_NEEDS_SPLIT flag say
                  the same thing. The flag says it in a sentence that names the
                  chore, so it wins; this row is the fallback for the case where
                  the pipeline set the field without emitting the flag. */}
              {d.is_multi_request && !multiRequestFlagged && (
                <Row label="Requests">
                  <span className="boss-chip boss-chip-warning">More than one</span>
                </Row>
              )}
            </div>

            {/* How it decided, in English. The mechanism fields behind this
                (`dept_source: llm`, `owner_mode: skill_based`) are the
                pipeline's own vocabulary and live under Technical details. */}
            {(methodSentence || escalation) && (
              <div className="mt-2 space-y-1.5">
                {methodSentence && (
                  <p className="text-xs text-boss-text-secondary leading-relaxed">
                    {methodSentence}
                  </p>
                )}
                {escalation && (
                  <p className="text-xs text-boss-text-secondary leading-relaxed">
                    {escalation}
                  </p>
                )}
              </div>
            )}
          </section>

          {/* Email summary */}
          <section className="space-y-0">
            <h3 className="text-xs font-semibold text-boss-text-secondary uppercase tracking-wider mb-2">
              Email
            </h3>
            <div className="glass-card p-3 text-sm space-y-0">
              <Row label="Subject">{d.subject || '—'}</Row>
              <Row label="Sender">{d.sender_address || '—'}</Row>
              <Row label="Distro">{d.distro || '—'}</Row>
              <Row label="Advisor">
                {(() => {
                  const a = describeAdvisor(d);
                  return (
                    <span
                      className={
                        a.tone === 'none' ? 'text-boss-text-secondary' : undefined
                      }
                    >
                      {a.text}
                    </span>
                  );
                })()}
              </Row>
              <Row label="Thread state">{d.thread_state || '—'}</Row>
            </div>
          </section>

          {/* Email body. Collapsed by default — most rows are judged from the
              subject, sender and distro alone, and an open long email would
              push the labelling controls off screen. No fetch: `body_text`
              already rides in on GET /decisions, so this reads straight off
              the decision object the drawer was handed.

              `null` and `""` are worded apart on purpose: a missing key means
              this decision predates n8n sending bodies at all, while an empty
              string means the email genuinely had no plain-text part. */}
          <CollapsibleSection title="Email body">
            {d.body_text == null && (
              <p className="text-sm text-boss-text-secondary">
                No email body was captured for this decision.
              </p>
            )}
            {d.body_text === '' && (
              <p className="text-sm text-boss-text-secondary">
                This email had no plain-text part.
              </p>
            )}
            {!!d.body_text && (
              // Plain text in a <pre>, never dangerouslySetInnerHTML — this is
              // customer-authored content, and the API only ever sends the
              // plain-text part for exactly this reason.
              <pre className="text-xs text-boss-text font-sans whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
                {d.body_text}
              </pre>
            )}
          </CollapsibleSection>

          {/* Ground-truth capture. Sits directly under the AI's answer so a
              labeller can compare and record without scrolling, and is
              rendered only for holders of ai_dispatcher.decisions.label —
              a user without it gets the read-only Human Review section
              below, not a disabled control.

              Keyed on the decision id so navigating to the next decision
              resets the form rather than carrying the previous row's
              selections forward. */}
          {canLabel && (
            <LabelPanel
              key={d.dispatch_item_id}
              decision={d}
              onLabeled={handleLabeled}
              onAdvance={onNext}
              hasNext={Boolean(onNext)}
              advanceAfterSave={advanceAfterSave}
              onAdvanceAfterSaveChange={setAdvanceAfterSave}
            />
          )}

          {/* Reasoning */}
          {d.reasoning && (
            <section>
              <h3 className="text-xs font-semibold text-boss-text-secondary uppercase tracking-wider mb-2">
                Reasoning
              </h3>
              <p className="text-sm text-boss-text leading-relaxed bg-boss-elevated border border-boss-border rounded-lg px-4 py-3">
                {d.reasoning}
              </p>
            </section>
          )}

          {/* Missing-info draft — Stage G output for low-confidence decisions.
              Persisted for audit only; the workflow never sends this to the
              advisor, so the label says so explicitly. */}
          {d.missing_info_draft_text && (
            <section>
              <h3 className="text-xs font-semibold text-boss-text-secondary uppercase tracking-wider mb-2">
                Missing-Info Draft (not sent)
              </h3>
              <p className="text-sm text-boss-text leading-relaxed bg-boss-elevated border border-boss-border rounded-lg px-4 py-3">
                {d.missing_info_draft_text}
              </p>
            </section>
          )}

          {/* Review notes — the flags, in English, split by whether anyone has
              to do something. Rendered as sentences rather than chips on
              purpose: boss-chip is nowrap + truncate, which was fine for
              `POSSIBLE_DUPLICATE` and would eat the readable version. */}
          {notes.length > 0 && (
            <section className="space-y-3">
              <h3 className="text-xs font-semibold text-boss-text-secondary uppercase tracking-wider">
                Review Notes
              </h3>

              {actionNotes.length > 0 && (
                <div className="boss-alert-danger rounded-lg px-3 py-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5">
                    Needs a person
                  </p>
                  <ul className="space-y-1.5">
                    {actionNotes.map((n, i) => (
                      <li key={`${n.raw}-${i}`} title={n.raw} className="flex gap-2">
                        <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="text-xs leading-relaxed">{n.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {contextNotes.length > 0 && (
                <ul className="space-y-1.5">
                  {contextNotes.map((n, i) => (
                    <li key={`${n.raw}-${i}`} title={n.raw} className="flex gap-2">
                      {n.severity === 'gap' ? (
                        <AlertCircle
                          size={14}
                          className="shrink-0 mt-0.5 text-amber-400"
                          aria-hidden="true"
                        />
                      ) : (
                        <Info
                          size={14}
                          className="shrink-0 mt-0.5 text-boss-text-muted"
                          aria-hidden="true"
                        />
                      )}
                      <span className="text-xs text-boss-text-secondary leading-relaxed">
                        {n.text}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {/* Splits (multi-request) */}
          {splits.length > 0 && (
            <CollapsibleSection title={`Splits (${splits.length})`} defaultOpen>
              <div className="space-y-2">
                {splits.map((s, i) => (
                  <div key={i} className="bg-boss-elevated rounded-lg p-3 text-xs space-y-1">
                    {s.provider && (
                      <p><span className="text-boss-text-secondary">Provider:</span> <span className="text-boss-text">{s.provider}</span></p>
                    )}
                    <p><span className="text-boss-text-secondary">Dept:</span> <span className="text-boss-text">{s.department}</span></p>
                    <p><span className="text-boss-text-secondary">Ticket type:</span> <span className="text-boss-text">{s.ticket_type}</span></p>
                    <p><span className="text-boss-text-secondary">Confidence:</span> <ConfidenceBadge val={s.confidence} /></p>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Human reconciliation */}
          <section>
            <h3 className="text-xs font-semibold text-boss-text-secondary uppercase tracking-wider mb-2">
              Human Review
            </h3>
            {(d.human_department || d.human_ticket_type || d.human_owner) ? (
              <div className="glass-card p-3 space-y-0">
                <Row label="H. Department">
                  <MatchIcon val={d.dept_match} field="Department" />
                  {d.human_department || <span className="text-boss-text-muted">Not answered</span>}
                </Row>
                <Row label="H. Ticket type">
                  <MatchIcon val={d.type_match} field="Ticket type" />
                  {d.human_ticket_type || <span className="text-boss-text-muted">Not answered</span>}
                </Row>
                <Row label="H. Owner">
                  <MatchIcon val={d.owner_match} field="Owner" />
                  {d.human_owner || <span className="text-boss-text-muted">Not answered</span>}
                </Row>
                {d.human_reasoning && (
                  <Row label="Reasoning">
                    <span className="whitespace-pre-wrap">{d.human_reasoning}</span>
                  </Row>
                )}
                {d.human_labeled_by && <Row label="Labelled by">{d.human_labeled_by}</Row>}
                {d.human_labeled_at && (
                  <Row label="Labelled at">{formatReceived(d.human_labeled_at)}</Row>
                )}
              </div>
            ) : (
              <p className="text-xs text-boss-text-muted italic">
                Not yet reconciled — human routing decision pending.
              </p>
            )}
            {/* An off-enum label is not a disagreement the model could have
                avoided — it named something the classifier has no value for.
                Saying which field, rather than just flagging the row, is what
                makes it actionable. */}
            {d.human_value_off_enum && (
              <div className="boss-alert-warning rounded-lg p-3 mt-2 flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <p className="text-xs text-boss-text">
                  {offEnumFieldLabels(d.human_off_enum_fields)} outside our taxonomy, so the AI
                  could not have produced this answer. Recorded verbatim and counted separately.
                </p>
              </div>
            )}

            {/* The owner icon above compares two values that are both on
                screen, which is honest. The aggregate owner rate is not —
                see AgreementSummary. */}
            <p className="text-[11px] text-boss-text-muted mt-2">
              Department and ticket type feed the agreement rate. Owner is recorded but not
              scored.
            </p>
          </section>

          {/* LLM calls */}
          {llmCalls.length > 0 && (
            <CollapsibleSection title={`LLM Calls (${llmCalls.length})`}>
              <div className="space-y-3">
                {llmCalls.map((call, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-boss-text font-medium">{call.model || `Call ${i + 1}`}</span>
                      <span className="text-boss-text-muted">
                        {call.tokens_in != null && `${call.tokens_in} in`}
                        {call.tokens_out != null && ` / ${call.tokens_out} out`}
                      </span>
                    </div>
                    {call.raw_response && (
                      <CollapsibleSection title="Raw response">
                        <pre className="text-xs text-boss-text-secondary overflow-auto max-h-48 whitespace-pre-wrap break-all">
                          {typeof call.raw_response === 'string'
                            ? call.raw_response
                            : JSON.stringify(call.raw_response, null, 2)}
                        </pre>
                      </CollapsibleSection>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* SLA */}
          {d.sla_due_at && (
            <section>
              <h3 className="text-xs font-semibold text-boss-text-secondary uppercase tracking-wider mb-2">
                SLA
              </h3>
              <p className="text-sm text-boss-text">Due: {formatReceived(d.sla_due_at)}</p>
            </section>
          )}

          {/* Technical details — the raw pipeline vocabulary, collapsed.
              Everything above is the translated form of these values; when a
              translation looks wrong, this is what you compare it against, and
              it is also the only place an unrecognised `dept_source` or model
              alias is still visible. Kept, not deleted. */}
          <CollapsibleSection title="Technical details">
            <div className="space-y-0">
              <Row label="Dept source">{d.dept_source || '—'}</Row>
              <Row label="Type source">{d.type_source || '—'}</Row>
              <Row label="Owner mode">{d.owner_mode || '—'}</Row>
              <Row label="AI model">{d.ai_final_source || '—'}</Row>
              <Row label="Escalation">{d.escalation_reason || '—'}</Row>
              <Row label="Raw flags">
                {flags.length > 0 ? (
                  <span className="font-mono break-all">{flags.join(', ')}</span>
                ) : (
                  '—'
                )}
              </Row>
              <Row label="Item ID">
                <span className="font-mono break-all">{d.dispatch_item_id || '—'}</span>
              </Row>
            </div>
          </CollapsibleSection>
        </div>
      </div>
    </>
  );
}
